import { Outlet, useParams, useLocation, Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { ChevronRight, RefreshCw, X } from "lucide-react";
import { DealSideNav, DealMobileNav } from "./DealNav";
import LoadingSpinner from "./LoadingSpinner";
import StatusBadge from "./StatusBadge";
import ChatPanel from "./ChatPanel";
import { fetchDeal, fetchScenarios, autoProject, type Deal, type ScenarioResponse } from "../api/client";
import { useToast } from "../contexts/ToastContext";

const CHAT_COLLAPSED_KEY = "autoval_chat_collapsed";

export default function DealLayout() {
  const { dealId } = useParams<{ dealId: string }>();
  const location = useLocation();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recalcDismissed, setRecalcDismissed] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const toast = useToast();

  // Chat collapsed state — persisted in localStorage. With no stored
  // preference, default to collapsed on small screens so the chat rail
  // doesn't crowd out page content on mobile.
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    const stored = localStorage.getItem(CHAT_COLLAPSED_KEY);
    if (stored !== null) return stored === "true";
    return window.innerWidth < 1024;
  });
  const toggleChat = useCallback(() => {
    setChatCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(CHAT_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  // Derive active tab from URL
  const activeTab = (() => {
    const path = location.pathname.split("/").pop() || "";
    const map: Record<string, string> = {
      "": "Dashboard", "rent-roll": "Rent Roll", "operating-statement": "Op. Statement",
      "reimbursements": "Reimbursements", "cash-flow": "Pro Forma", "expenses": "Expenses", "assumptions": "Assumptions", "exports": "Exports",
      "market-leasing": "Market Leasing", "scenarios": "Scenarios",
      "audit": "Audit", "documents": "Documents", "activity": "Activity",
      "settings": "Settings", "admin": "Admin",
      "verify": "Verify", "source-data": "Source Data", "audit-trail": "Audit Trail",
    };
    return map[path] || "Dashboard";
  })();

  useEffect(() => {
    if (!dealId) return;
    fetchScenarios(dealId).then(setScenarios).catch(() => {});
  }, [dealId]);

  const activeScenario = scenarios.find(s => s.projection_result) ?? scenarios[0];

  const refreshAfterChat = useCallback(async () => {
    if (!dealId) return;
    const [freshDeal, freshScenarios] = await Promise.all([
      fetchDeal(dealId), fetchScenarios(dealId),
    ]);
    setDeal(freshDeal);
    setScenarios(freshScenarios);
  }, [dealId]);

  const needsRecalc = deal?.property_data?._needs_recalculation && deal?.property_data?._last_projected_at && !recalcDismissed;

  const handleRecalc = async () => {
    if (!dealId) return;
    setRecalculating(true);
    try {
      await autoProject(dealId);
      const fresh = await fetchDeal(dealId);
      setDeal(fresh);
      setRecalcDismissed(false);
      toast.success("Projections updated");
    } catch {
      toast.error("Recalculation failed");
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    setError(null);
    fetchDeal(dealId)
      .then(setDeal)
      .catch((e) => {
        setDeal(null);
        setError(e instanceof Error ? e.message : "Failed to load deal");
      })
      .finally(() => setLoading(false));
  }, [dealId]);

  if (error)
    return (
      <div className="max-w-lg mx-auto py-16 px-8 text-center">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8">
          <p className="text-red-700 font-medium mb-2">Failed to load deal</p>
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </div>
    );
  if (!loading && !deal)
    return (
      <div className="py-16 text-center text-bark-400">Deal not found.</div>
    );

  const showChatPanel = !loading && deal && activeScenario;

  return (
    <div className="animate-fade-in flex flex-col h-screen">
      <div className="bg-white border-b border-grove-300 shrink-0">
        <div className="max-w-[1600px] mx-auto px-8 py-6">
          <div className="flex items-center gap-2 text-sm text-bark-400 mb-3">
            <Link to="/" className="hover:text-forest-700 transition-colors">Properties</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-bark-700 font-medium">{loading ? "Loading\u2026" : deal!.name}</span>
          </div>
          {loading ? (
            <div className="h-8 w-48 bg-grove-200 rounded animate-pulse" />
          ) : (
            <>
              <div className="flex items-center gap-4">
                <h1 className="font-display text-2xl font-semibold text-bark-800 tracking-tight">{deal!.name}</h1>
                <StatusBadge status={deal!.status} />
              </div>
              {deal!.address && <p className="text-sm text-bark-400 mt-1">{deal!.address}</p>}
            </>
          )}
        </div>
      </div>

      <DealMobileNav />

      {needsRecalc && (
        <div className="bg-honey-50 border-b border-honey-200 print:hidden shrink-0">
          <div className="max-w-[1600px] mx-auto px-8 py-2.5 flex items-center justify-between">
            <span className="text-sm text-honey-800">Data has changed since the last projection.</span>
            <div className="flex items-center gap-2">
              <button onClick={handleRecalc} disabled={recalculating} className="flex items-center gap-1.5 px-3 py-1.5 bg-honey-600 text-white text-xs font-semibold rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all">
                <RefreshCw className={`w-3.5 h-3.5 ${recalculating ? "animate-spin" : ""}`} />
                {recalculating ? "Updating..." : "Recalculate"}
              </button>
              <button onClick={() => setRecalcDismissed(true)} className="text-honey-500 hover:text-honey-700 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Side nav + main content + chat sidebar */}
      <div className="flex flex-1 min-h-0">
        <DealSideNav />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-8">
            {loading ? <LoadingSpinner /> : <Outlet context={{ deal, setDeal, scenarios, setScenarios }} />}
          </div>
        </div>

        {/* AutoVal Chat — right sidebar, full height */}
        {showChatPanel && (
          <div className={`shrink-0 print:hidden ${chatCollapsed ? "w-10" : "w-80"} transition-all duration-200 sticky top-0 h-[calc(100vh-3.5rem)] overflow-hidden`}>
            <ChatPanel
              dealId={dealId!}
              scenarioId={activeScenario.id}
              scenarioName={activeScenario.name}
              scenarioAssumptions={activeScenario.assumptions ?? null}
              dealPropertyData={(deal.property_data as unknown as Record<string, unknown>) ?? null}
              activeTab={activeTab}
              collapsed={chatCollapsed}
              onToggle={toggleChat}
              onProjectionUpdated={refreshAfterChat}
              onScenarioCreated={refreshAfterChat}
            />
          </div>
        )}
      </div>
    </div>
  );
}
